"use client";

import { toast } from "@/components/ui";
import { Button } from "@/components/ui/primitives/button";
import { Field, Fieldset, Label } from "@/components/ui/primitives/fieldset";
import { Input } from "@/components/ui/primitives/input";
import { PasswordInput } from "@/components/ui/primitives/password-input";
import { signinAction } from "@/lib/actions/auth";
import { buildAuthFormFallbackAction } from "@/lib/auth/form-fallback";
import {
  SIGNIN_URL_ERROR_MESSAGES,
  signInUrlSuccessMessage,
} from "@/lib/constants/auth";
import { useHydrated } from "@/lib/hooks/useHydrated";
import { applyServerErrors } from "@/lib/utils/apply-server-errors";
import {
  signinSchema,
  type SigninForm as SigninFormType,
} from "@/lib/validation/forms";
import { zodResolver } from "@hookform/resolvers/zod";
import { isRedirectError } from "next/dist/client/components/redirect-error";
import Link from "next/link";
import {
  unstable_rethrow as rethrow,
  usePathname,
  useRouter,
} from "next/navigation";
import { useEffect, useRef, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { AuthFormCard } from "../../components/auth-form-card";
import {
  AUTH_FORM_LOADING_HINT_ID,
  FormLoadingHint,
} from "../../components/form-loading-hint";
import { MobileAppDownloadLinks } from "../../components/mobile-app-download-links";

/** Stable id so a remount updates this toast instead of stacking a second one. */
const SIGNIN_URL_MESSAGE_TOAST_ID = "signin-url-message";

interface SignInFormProps {
  /** Where to redirect after successful sign-in (extracted server-side) */
  readonly callbackUrl: string;
  /** URL error parameter, e.g. "SessionExpired" (extracted server-side) */
  readonly errorCode: string | null;
  /** URL success message, e.g. from password reset redirect (extracted server-side) */
  readonly urlMessage: string | null;
}

export function SignInForm({
  callbackUrl,
  errorCode,
  urlMessage,
}: SignInFormProps) {
  // Fields stay disabled until onSubmit is attached: disabled controls are
  // excluded from a native submit, so credentials can't reach the URL.
  const hydrated = useHydrated();
  const router = useRouter();
  const pathname = usePathname();
  const form = useForm<SigninFormType>({
    resolver: zodResolver(signinSchema),
    defaultValues: { email: "", password: "" },
    mode: "onBlur",
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = form;

  // Error toasts this form creates. The URL success toast is intentionally
  // excluded: dismissing it on cleanup wipes the signed-out message when
  // soft navigation remounts this form.
  const formToastIds = useRef<Array<string | number>>([]);

  // Known URL success messages (signup, password reset, sign-out). A stable
  // id updates this toast on remount. Cleanup does not dismiss it: that wipe
  // is what dropped the signed-out message. `?message=` is removed so a
  // refresh does not show it again.
  useEffect(() => {
    const message = signInUrlSuccessMessage(urlMessage);
    if (message) {
      toast.success(message, { id: SIGNIN_URL_MESSAGE_TOAST_ID });
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has("message")) return;
    params.delete("message");
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    });
  }, [urlMessage, router, pathname]);

  useEffect(() => {
    const ids = formToastIds.current;
    return () => {
      for (const id of ids) {
        toast.dismiss(id);
      }
    };
  }, []);

  useEffect(() => {
    if (errorCode) {
      const message = SIGNIN_URL_ERROR_MESSAGES[errorCode];
      if (message) {
        setError("root", { type: "url", message });
      }
    }
  }, [errorCode, setError]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      // signinAction performs a server-side redirect() on success, which
      // surfaces to the client as a NEXT_REDIRECT throw that `rethrow`
      // propagates so the router navigates in-place. We only observe the
      // `result` on the failure path.
      const result = await signinAction({ ...data, callbackUrl });
      applyServerErrors(setError, result, {
        onFormError: (message) => {
          setError("root", { type: "server", message });
        },
      });
    } catch (error) {
      // Successful sign-in throws NEXT_REDIRECT. Dismiss the URL toast
      // before rethrow, or it stays on the next page for Sonner's lifetime.
      if (isRedirectError(error)) {
        toast.dismiss(SIGNIN_URL_MESSAGE_TOAST_ID);
      }
      rethrow(error);
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      setError("root", { type: "unknown", message });
      const id = toast.error(message);
      if (typeof id === "string" || typeof id === "number") {
        formToastIds.current.push(id);
      }
    }
  });

  const rootError = errors.root?.message;

  function submitSignIn(event: FormEvent<HTMLFormElement>) {
    // Server-action redirects are rejected with `handled` set: the router has
    // already navigated. Swallow that rejection here so it does not surface
    // as an unhandled NEXT_REDIRECT after the toast is dismissed.
    void Promise.resolve(onSubmit(event)).catch((error: unknown) => {
      if (isRedirectError(error) && "handled" in error) return;
      throw error;
    });
  }

  return (
    <AuthFormCard
      title="Welcome back"
      subtitle="Your analyses and insights are waiting."
    >
      <form
        method="post"
        action={buildAuthFormFallbackAction("/signin", { callbackUrl })}
        className="space-y-4"
        onSubmit={submitSignIn}
        noValidate
        aria-busy={!hydrated || undefined}
        aria-describedby={!hydrated ? AUTH_FORM_LOADING_HINT_ID : undefined}
      >
        <Fieldset disabled={!hydrated} className="min-w-0 space-y-4">
          <Field error={errors.email?.message}>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@company.com"
              invalid={!!errors.email}
              disabled={isSubmitting}
              autoComplete="email"
              {...register("email")}
            />
          </Field>

          <Field error={errors.password?.message}>
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              placeholder="Enter your password"
              invalid={!!errors.password}
              disabled={isSubmitting}
              autoComplete="current-password"
              {...register("password")}
            />
          </Field>

          {rootError && (
            <div
              className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              {rootError}
            </div>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Sign in
            </Button>
          </div>
        </Fieldset>

        {!hydrated && <FormLoadingHint />}

        <div className="sr-only" aria-live="polite">
          {isSubmitting && "Signing in..."}
        </div>

        <p className="text-center text-[12px] text-text-tertiary">
          <Link
            href="/forgot-password"
            className="hover:text-text-secondary hover:underline underline-offset-2 transition-colors duration-200"
            aria-label="Reset your password"
          >
            Forgot password?
          </Link>
        </p>
      </form>
      <MobileAppDownloadLinks />
    </AuthFormCard>
  );
}
